import { Component } from '@theme/component';

const SDK_SRC = new URL('sesami-sdk.js', import.meta.url).href;

const getOrdinalSuffix = (number) => {
  const tens = number % 100;
  const ones = number % 10;

  if (ones === 1 && tens !== 11) return 'st';
  if (ones === 2 && tens !== 12) return 'nd';
  if (ones === 3 && tens !== 13) return 'rd';
  return 'th';
};

const friendlyDateString = (isoOrDate, locale) => {
  const date = new Date(isoOrDate);
  if (Number.isNaN(date.getTime())) return '';

  const day = date.getDate();
  const suffix = getOrdinalSuffix(day);
  const month = new Intl.DateTimeFormat(locale || 'en-US', { month: 'long' }).format(date);
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

const dayName = (date, locale) => new Intl.DateTimeFormat(locale || 'en-US', { weekday: 'short' }).format(new Date(date));

const clearChildren = (element) => {
  if (!element) return;
  while (element.firstChild) element.removeChild(element.firstChild);
};

const slotDisplayTime = (startTime) => {
  if (!startTime) return '';
  const parts = String(startTime).split(' ');
  return parts.length >= 2 ? parts[1] : String(startTime);
};

const slotDisplayDate = (startTime, locale) => {
  if (!startTime) return '';
  const parts = String(startTime).split(' ');
  if (!parts[0]) return '';
  return friendlyDateString(`${parts[0]}T00:00:00`, locale);
};

class SesamiBookingComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Promise<void> | null} */
  #bootPromise = null;

  /** @type {any} */
  #sdk = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLButtonElement | null} */
  #realAddToCartButton = null;

  /** @type {HTMLButtonElement | null} */
  #proxyButton = null;

  /** @type {boolean} */
  #isSubmittingReservation = false;

  /** @type {any | null} */
  #pendingSlot = null;

  /** @type {any | null} */
  #pendingDay = null;

  /** @type {string} */
  #locale = 'en';

  /** @type {string | null} */
  #timezone = null;

  /** @type {number} */
  #renderRevision = 0;

  requiredRefs = ['mount'];

  connectedCallback() {
    super.connectedCallback();

    if (!this.#bootPromise) {
      this.#bootPromise = this.#boot().catch((error) => {
        console.error('Sesami booking boot failed', error);
        this.#renderError('Sesami failed to initialise. Please try again later.');
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    document.body.classList.remove('booking-slot-selected');
    this.#proxyButton = null;
    this.#realAddToCartButton = null;
  }

  get #mount() {
    return this.refs.mount instanceof HTMLElement ? this.refs.mount : null;
  }

  #readConfig() {
    const productId = Number(this.dataset.productId || 0);
    const variantId = Number(this.dataset.variantId || 0);
    const shopId = Number(this.dataset.shopId || 0);
    const timezone = this.dataset.liquidTimezone || this.dataset.timezone || null;
    const appointmentProductId = Number(this.dataset.appointmentProductId || 0) || null;

    return {
      productId,
      variantId,
      shopId,
      timezone,
      appointmentProductId,
      locale: this.dataset.locale || document.documentElement.lang || 'en',
      formSelector: this.dataset.formSelector || '[data-type="add-to-cart-form"]',
    };
  }

  async #boot() {
    this.#abortController = new AbortController();

    const config = this.#readConfig();
    this.#locale = config.locale;
    this.#timezone = config.timezone;

    this.#form = this.#resolveForm(config.formSelector);

    if (this.#form) {
      this.#setAddToCartDisabledForForm(this.#form, true);
      this.#syncConfirmationGroupVisibility(true);
    }

    if (!config.productId || !config.variantId) {
      this.#renderError('Sesami: missing product or variant information.');
      return;
    }

    // Check cart on load — if the appointment product is already there, skip Sesami entirely.
    if (config.appointmentProductId) {
      try {
        const cart = await this.#fetchCart();
        const appointmentItem = Array.isArray(cart?.items)
          ? cart.items.find((item) => item.product_id === config.appointmentProductId)
          : null;

        if (appointmentItem) {
          this.#showAlreadyInCart();

          // Restore the date/time message from the cart item's line properties
          const props = appointmentItem.properties || {};
          const bookingDate = props['Date'] || '';
          const bookingTime = props['Time'] || '';
          if (bookingDate) {
            const alreadyInCartEl = this.querySelector('.booking-already-in-cart');
            const makeSelectionEndEl = document.querySelector('.make-selection-end');
            const reservedMsgEl = alreadyInCartEl?.querySelector('.sesami-day-selected.h3');

            if (reservedMsgEl instanceof HTMLElement && makeSelectionEndEl instanceof HTMLElement) {
              makeSelectionEndEl.prepend(reservedMsgEl);
              const formattedDate = friendlyDateString(`${bookingDate}T00:00:00`, this.#locale);
              reservedMsgEl.textContent = bookingTime
                ? `Your date and time are reserved for ${formattedDate} at ${bookingTime}`
                : `Your date is reserved for ${formattedDate}`;
            }
          }

          return;
        }
      } catch (cartCheckError) {
        console.warn('Sesami: cart pre-check failed, continuing with calendar', cartCheckError);
      }
    }

    const sdkLoadResult = await this.#waitForSDK();
    if (!sdkLoadResult.loaded) {
      this.#renderConsentBlockedState(sdkLoadResult.reason);
      return;
    }

    const sdk = this.#createSdk(config);
    await sdk.init();
    this.#sdk = sdk;
    window.sesami = sdk;

    if (!sdk.data || !Array.isArray(sdk.data.labels) || !Array.isArray(sdk.data.rows)) {
      this.#renderError('Sesami returned no calendar data.');
      return;
    }

    await this.#renderFromData(sdk.data);
    this.#attachAddToCartReserveHandler();
    this.#setBookingStep(0);
  }

  #resolveForm(formSelector) {
    const form = document.querySelector(formSelector);
    return form instanceof HTMLFormElement ? form : null;
  }

  #createSdk(config) {
    const SesamiCtor = window.SesamiSDK || window.Sesami;
    if (typeof SesamiCtor !== 'function') {
      throw new Error('Sesami SDK not found on window.');
    }

    return new SesamiCtor({
      shopId: String(config.shopId),
      productId: String(config.productId),
      variantId: String(config.variantId),
      quantity: 1,
      autoLoad: true,
      timezone: config.timezone || undefined,
      locale: config.locale,
    });
  }

  #waitForSDK() {
    return new Promise((resolve) => {
      if (window.SesamiSDK || window.Sesami) {
        resolve({ loaded: true });
        return;
      }

      /** @type {boolean} */
      let settled = false;

      const finish = (result) => {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      const inject = () => {
        if (window.SesamiSDK || window.Sesami) {
          finish({ loaded: true });
          return;
        }

        const script = document.createElement('script');
        script.src = SDK_SRC;
        script.async = true;
        script.onload = () => finish({ loaded: true });
        script.onerror = () => finish({ loaded: false, reason: 'sdk_load_failed' });
        document.head.appendChild(script);
      };

      const hasTermly = Boolean(window.Termly && typeof window.Termly.getConsentState === 'function');

      if (!hasTermly) {
        inject();
        return;
      }

      const evaluateConsent = () => {
        const consentState = this.#getTermlyConsentState();
        if (!consentState) return false;

        if (this.#hasSesamiConsent(consentState)) {
          inject();
          return true;
        }

        if (this.#isTermlyConsentFinal(consentState)) {
          const reason = this.#isUnclassifiedDenied(consentState) ? 'termly_unclassified_blocked' : 'termly_blocked';
          finish({ loaded: false, reason });
          return true;
        }

        return false;
      };

      if (evaluateConsent()) return;

      const onConsent = () => {
        evaluateConsent();
      };

      document.addEventListener('termly:consent', onConsent, { signal: this.#abortController?.signal });
      document.addEventListener('termly:preferences', onConsent, { signal: this.#abortController?.signal });

      const timeout = window.setTimeout(() => {
        if (!settled) {
          finish({ loaded: false, reason: 'termly_timeout' });
        }
      }, 10000);

      this.#abortController?.signal.addEventListener(
        'abort',
        () => {
          window.clearTimeout(timeout);
          finish({ loaded: false, reason: 'aborted' });
        },
        { once: true },
      );
    });
  }

  #getTermlyConsentState() {
    try {
      if (!window.Termly || typeof window.Termly.getConsentState !== 'function') return null;
      return window.Termly.getConsentState();
    } catch (error) {
      console.warn('Sesami: failed to read Termly consent state', error);
      return null;
    }
  }

  #collectBooleanMatches(value, keyPattern, results = []) {
    if (value == null) return results;

    if (Array.isArray(value)) {
      for (const entry of value) {
        this.#collectBooleanMatches(entry, keyPattern, results);
      }
      return results;
    }

    if (typeof value !== 'object') return results;

    for (const [key, child] of Object.entries(value)) {
      if (keyPattern.test(key) && typeof child === 'boolean') {
        results.push(child);
      }

      if (child && typeof child === 'object') {
        this.#collectBooleanMatches(child, keyPattern, results);
      }
    }

    return results;
  }

  #hasSesamiConsent(consentState) {
    if (consentState === true) return true;

    if (!consentState || typeof consentState !== 'object') return false;

    const acceptedKeys = this.#collectBooleanMatches(
      consentState,
      /(functional|preferences|performance|analytics|marketing|advertising|social|unclassified|uncategorized)/i,
    );

    return acceptedKeys.some(Boolean);
  }

  #isUnclassifiedDenied(consentState) {
    if (!consentState || typeof consentState !== 'object') return false;

    const unclassifiedKeys = this.#collectBooleanMatches(consentState, /(unclassified|uncategorized)/i);
    return unclassifiedKeys.length > 0 && unclassifiedKeys.every((value) => value === false);
  }

  #isTermlyConsentFinal(consentState) {
    if (!consentState || typeof consentState !== 'object') return false;

    const finalFlags = this.#collectBooleanMatches(
      consentState,
      /(consented|has_consented|is_consented|is_set|accepted)/i,
    );

    if (finalFlags.some(Boolean)) return true;
    return this.#isUnclassifiedDenied(consentState);
  }

  #renderError(message) {
    const mount = this.#mount;
    if (!mount) return;

    mount.innerHTML = `
      <div class="booking-already-in-cart active" role="status">
        <p class="booking-already-in-cart__message">${message}</p>
      </div>
    `;
    document.querySelector('.initial-text').classList.add('hidden');
    document.querySelector('.product-form-buttons').classList.add('hidden');
  }

  #openConsentPreferencesModal() {
    const callModal = (modalFn) => {
      try {
        const result = modalFn();

        // Some hosts return a Promise here; prevent unhandled rejections from bubbling.
        if (result && typeof result.then === 'function') {
          result.catch((error) => {
            console.warn('Sesami: displayPreferenceModal promise rejected', error);
          });
        }

        return true;
      } catch (error) {
        console.warn('Sesami: displayPreferenceModal failed', error);
        return false;
      }
    };

    try {
      if (typeof window.displayPreferenceModal === 'function') {
        return callModal(window.displayPreferenceModal);
      }

      if (typeof globalThis.displayPreferenceModal === 'function') {
        return callModal(globalThis.displayPreferenceModal);
      }
    } catch (error) {
      console.warn('Sesami: displayPreferenceModal lookup failed', error);
    }

    return false;
  }

  #renderConsentBlockedState(reason) {
    const message =
      reason === 'termly_unclassified_blocked'
        ? 'Booking needs cookie consent. Please enable the <b>Performance and Functionality Cookies</b> category to continue. <br/> <b>Please refresh this page after updating your preferences.</b>'
        : 'Booking needs cookie consent. Please review your <b>Performance and Functionality Cookies</b> preferences to continue. <br/> <b>Please refresh this page after updating your preferences.</b>';

    this.#renderError(message);
    //this.#openConsentPreferencesModal(); do not open automatically

    const mount = this.#mount;
    if (!mount) return;

    const container = mount.querySelector('.booking-already-in-cart');
    if (!(container instanceof HTMLElement)) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.textContent = 'Cookie preferences';
    button.addEventListener('click', () => {
      this.#openConsentPreferencesModal();
    });

    container.appendChild(button);
  }

  /** Swaps the calendar view for the already-in-cart confirmation state. */
  #showAlreadyInCart() {
    const mount = this.#mount;
    if (mount) mount.classList.remove('active');

    this.#syncConfirmationGroupVisibility(true);

    const alreadyInCartEl = this.querySelector('.booking-already-in-cart');
    const makeSelectionEndEl = document.querySelector('.make-selection-end');
    const reservedMsgEl = alreadyInCartEl?.querySelector('.sesami-day-selected.h3');

    if (reservedMsgEl instanceof HTMLElement && makeSelectionEndEl instanceof HTMLElement) {
      makeSelectionEndEl.prepend(reservedMsgEl);
    }

    if (alreadyInCartEl instanceof HTMLElement) alreadyInCartEl.classList.add('active');

    const initialText = document.querySelector('.group-block.initial-text');
    if (initialText instanceof HTMLElement) initialText.style.display = 'none';

    const addToCartComp = document.querySelector('add-to-cart-component');
    if (addToCartComp instanceof HTMLElement) addToCartComp.style.display = 'none';

    this.#setBookingStep(1);
  }

  /**
   * Advances the connected announcement bar to the given step index.
   * Targets only the bar that has data-booking-step-title so regular
   * announcement bars on other pages are not affected.
   * @param {number} stepIndex
   */

  #setBookingStep(stepIndex) {
    const bar = document.querySelector('[data-booking-step-title] announcement-bar-component');
    if (bar && typeof bar.current !== 'undefined') {
      bar.current = stepIndex;
    }
  }

  #syncConfirmationGroupVisibility(forceDisabled = null) {
    const isDisabled =
      forceDisabled != null
        ? forceDisabled
        : !(this.#proxyButton instanceof HTMLButtonElement) || this.#proxyButton.disabled;

    document.body.classList.toggle('booking-slot-selected', !isDisabled);
  }

  #setAddToCartDisabledForForm(form, disabled) {
    try {
      if (!form) return;

      const proxyButton = form.querySelector('[data-sesami-proxy="true"]');
      if (proxyButton instanceof HTMLButtonElement) {
        proxyButton.disabled = disabled;
        return;
      }

      const addToCartButton = this.#findAddToCartButton(form);
      if (addToCartButton) {
        addToCartButton.disabled = disabled;
      }

      const addToCartComponent = form.querySelector('add-to-cart-component');
      if (addToCartComponent) {
        if (disabled && typeof addToCartComponent.disable === 'function') {
          addToCartComponent.disable();
        }

        if (!disabled && typeof addToCartComponent.enable === 'function') {
          addToCartComponent.enable();
        }
      }
    } catch (error) {
      console.warn('Sesami: failed to toggle add-to-cart state', error);
    }
  }

  #findAddToCartButton(form) {
    if (!form) return null;
    const button = form.querySelector('[ref="addToCartButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  #ensureHiddenFieldsWrapper() {
    if (!this.#form) return null;

    let wrapper = this.#form.querySelector('.sesami-hidden-fields-wrapper');
    if (wrapper instanceof HTMLElement) return wrapper;

    wrapper = document.createElement('div');
    wrapper.className = 'sesami-hidden-fields-wrapper';
    this.#form.appendChild(wrapper);
    return wrapper;
  }

  #upsertHiddenField(wrapper, name, value, attributeName = null) {
    if (!wrapper) return;

    let input = wrapper.querySelector(`[name="${name}"]`);
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      if (attributeName) input.setAttribute(attributeName, '');
      wrapper.appendChild(input);
    }

    input.value = value == null ? '' : String(value);
  }

  #upsertSesamiHiddenFields(slot, reservationToken, bookingOptions = null) {
    const wrapper = this.#ensureHiddenFieldsWrapper();
    if (!wrapper || !this.#form) return;

    const startTimeParts = String(slot?.startTime || '').split(' ');
    const bookingDate = startTimeParts[0] || '';
    const bookingTime = startTimeParts[1] || '';

    const hiddenFields = {
      Date: bookingDate,
      Time: bookingTime,
      Timezone: this.#timezone || '',
      _reservation_token: reservationToken,
      _locale: this.#locale,
      remainingSlots: slot?.remainingSlots != null ? slot.remainingSlots : 1,
    };

    const attributeMap = {
      Date: 'sesami-hidden-date',
      Time: 'sesami-hidden-time',
      Timezone: 'sesami-hidden-timezone',
      _reservation_token: 'sesami-hidden-reservation-token',
      _locale: 'sesami-hidden-locale',
      remainingSlots: 'sesami-hidden-remaining-slots',
    };

    for (const [key, value] of Object.entries(hiddenFields)) {
      const fieldName = key === 'remainingSlots' ? 'remainingSlots' : `properties[${key}]`;
      this.#upsertHiddenField(wrapper, fieldName, value, attributeMap[key]);
    }

    if (bookingOptions) {
      this.dispatchEvent(
        new CustomEvent('sesami:booking-ready', {
          bubbles: true,
          detail: {
            slot,
            reservationToken,
            bookingOptions,
          },
        }),
      );
    }
  }

  #buildBookingOptionsFromCart(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    return {
      properties: items.map((item) => ({
        name: item?.title || `Variant ${item?.variant_id || 'unknown'}`,
        value: String(item?.variant_id || ''),
      })),
    };
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  async #renderFromData(data) {
    const mount = this.#mount;
    if (!mount) return;

    clearChildren(mount);

    this.#renderRevision += 1;
    const renderId = this.#renderRevision;
    const renderIdString = String(renderId);

    if (!this.#sdk._initialMonth) {
      const nowValue = this.#sdk.now || (data.labels && data.labels[0]) || new Date();
      this.#sdk._initialMonth = new Date(nowValue);
      this.#sdk._initialMonth.setDate(1);
      this.#sdk._initialMonth.setHours(0, 0, 0, 0);
    }

    const monthStart = (date) => {
      const result = new Date(date);
      result.setDate(1);
      result.setHours(0, 0, 0, 0);
      return result;
    };

    const isSameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

    const nav = document.createElement('div');
    nav.className = 'sesami-nav';

    const leftWrap = document.createElement('div');
    leftWrap.className = 'left';

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'button-unstyled';
    prevBtn.innerHTML = '<span class="svg-wrapper icon-caret icon-caret--backward">‹</span>';
    prevBtn.disabled = true;
    prevBtn.title = 'Go to previous month';
    leftWrap.appendChild(prevBtn);

    const monthLabel = document.createElement('div');
    monthLabel.className = 'sesami-month-label';

    const currentNow = this.#sdk.now ? new Date(this.#sdk.now) : (data.labels && data.labels[0]) || new Date();
    monthLabel.textContent = new Intl.DateTimeFormat(this.#locale || 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(currentNow);

    const rightWrap = document.createElement('div');
    rightWrap.className = 'right';

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'button-unstyled';
    nextBtn.innerHTML = '<span class="svg-wrapper icon-caret icon-caret--forward">›</span>';
    nextBtn.title = 'Go to next month';
    rightWrap.appendChild(nextBtn);

    nav.append(leftWrap, monthLabel, rightWrap);
    mount.appendChild(nav);

    const wrap = document.createElement('div');
    wrap.className = 'sesami-calendar-wrapper';

    const labelsEl = document.createElement('ul');
    labelsEl.className = 'sesami-weekdays';
    for (const labelDate of data.labels) {
      const li = document.createElement('li');
      li.textContent = dayName(labelDate, this.#locale);
      labelsEl.appendChild(li);
    }
    wrap.appendChild(labelsEl);

    const grid = document.createElement('div');
    grid.className = 'sesami-rows';
    grid.style.gridTemplateColumns = `repeat(${data.labels.length}, 1fr)`;
    wrap.appendChild(grid);

    const details = document.createElement('div');
    details.className = 'sesami-details';

    const daysMeta = [];

    for (const row of data.rows) {
      for (const day of row) {
        const cell = document.createElement('button');
        cell.type = 'button';
        const isInactive = Boolean(day.isPast || !day.isCurrentMonth);
        cell.classList.add(isInactive ? 'sesami-day-inactive' : 'sesami-day-active');
        cell.disabled = true;

        const cellDateString = typeof day.date === 'string' ? day.date.split('T')[0] : null;
        const dayNumber = cellDateString ? parseInt(cellDateString.split('-')[2], 10) : new Date(day.date).getDate();
        cell.textContent = String(dayNumber);

        const badge = document.createElement('div');
        const dayId = String(day.date);
        badge.className = 'sesami-badge';
        badge.setAttribute('role', 'status');
        badge.setAttribute('aria-label', 'Loading');
        badge.setAttribute('title', 'Loading…');
        badge.dataset.day = dayId;
        badge.dataset.render = renderIdString;
        cell.appendChild(badge);
        grid.appendChild(cell);

        daysMeta.push({ day, badge, cell, dayId, isInactive });

        cell.addEventListener(
          'click',
          async () => {
            grid
              .querySelectorAll('.sesami-day-selected')
              .forEach((element) => element.classList.remove('sesami-day-selected'));
            cell.classList.add('sesami-day-selected');

            this.#pendingSlot = null;
            this.#pendingDay = day;
            this.#setAddToCartDisabledForForm(this.#form, true);
            this.#syncConfirmationGroupVisibility(true);

            const existingPreview = mount.querySelector('.sesami-selected-preview');
            if (existingPreview) existingPreview.remove();

            const wrapper = this.#ensureHiddenFieldsWrapper();
            const tokenInput = wrapper?.querySelector('[name="properties[_reservation_token]"]');
            if (tokenInput instanceof HTMLInputElement) tokenInput.value = '';

            clearChildren(details);

            const header = document.createElement('div');
            header.className = 'sesami-slot-title';
            header.textContent = `Available times ${slotDisplayDate(day.date, this.#locale)}`;
            details.appendChild(header);

            const loading = document.createElement('div');
            loading.textContent = 'Loading…';
            details.appendChild(loading);

            try {
              const slots = await day.getAvailabilities();
              loading.remove();

              if (!slots || slots.length === 0) {
                const empty = document.createElement('div');
                empty.textContent = 'No available slots for this day.';
                details.appendChild(empty);
                return;
              }

              const slotList = document.createElement('ul');
              slotList.className = 'sesami-slot-list';

              for (const slot of slots) {
                const li = document.createElement('li');
                const slotButton = document.createElement('button');
                slotButton.type = 'button';
                slotButton.className = 'button button-secondary sesami-slot-time';
                slotButton.textContent = slotDisplayTime(slot.startTime);

                if (slot.status !== 'available' || slot.remainingSlots < 1) {
                  slotButton.disabled = true;
                }

                slotButton.addEventListener('click', () => {
                  slotList
                    .querySelectorAll('.sesami-slot-time-selected')
                    .forEach((element) => element.classList.remove('sesami-slot-time-selected'));
                  slotButton.classList.add('sesami-slot-time-selected');

                  this.#isSubmittingReservation = false;
                  this.#pendingSlot = slot;

                  const tokenInputAfterSelection = this.#ensureHiddenFieldsWrapper()?.querySelector(
                    '[name="properties[_reservation_token]"]',
                  );
                  if (tokenInputAfterSelection instanceof HTMLInputElement) tokenInputAfterSelection.value = '';

                  this.#setAddToCartDisabledForForm(this.#form, false);

                  this.#syncConfirmationGroupVisibility(false);

                  const existingSelectedPreview = mount.querySelector('.sesami-selected-preview');
                  if (existingSelectedPreview) existingSelectedPreview.remove();

                  const selectedPreview = document.createElement('div');
                  selectedPreview.className = 'sesami-selected-preview';
                  selectedPreview.textContent = `Selected: ${slotDisplayDate(slot.startTime, this.#locale)} at ${slotDisplayTime(slot.startTime)}`;
                  mount.appendChild(selectedPreview);

                  // Pre-populate the reservation message inside booking-already-in-cart.
                  // Scoped to .booking-already-in-cart to avoid matching the .sesami-day-selected
                  // class that is also applied to the selected calendar day button.
                  const alreadyInCartEl = this.querySelector('.booking-already-in-cart');
                  const reservedMsgEl = alreadyInCartEl?.querySelector('.sesami-day-selected');
                  if (reservedMsgEl instanceof HTMLElement) {
                    reservedMsgEl.textContent = `Your date and time are reserved for ${slotDisplayDate(slot.startTime, this.#locale)} at ${slotDisplayTime(slot.startTime)}`;
                  }

                  this.dispatchEvent(
                    new CustomEvent('sesami:slot-selected', {
                      bubbles: true,
                      detail: {
                        slot,
                      },
                    }),
                  );
                });

                li.appendChild(slotButton);
                slotList.appendChild(li);
              }

              details.appendChild(slotList);
            } catch (error) {
              loading.remove();
              const errorElement = document.createElement('div');
              errorElement.style.color = '#c33';
              errorElement.textContent = 'Failed to load slots. Please try again.';
              details.appendChild(errorElement);
              console.error('Sesami getAvailabilities error', error);
            }
          },
          { signal: this.#abortController?.signal },
        );
      }
    }

    wrap.appendChild(details);
    mount.appendChild(wrap);

    const updatePrevNextState = () => {
      const now = this.#sdk.now ? new Date(this.#sdk.now) : (data.labels && data.labels[0]) || new Date();
      const nowStart = monthStart(now);
      prevBtn.disabled = isSameMonth(nowStart, monthStart(this.#sdk._initialMonth));
      monthLabel.textContent = new Intl.DateTimeFormat(this.#locale || 'en-US', {
        month: 'long',
        year: 'numeric',
      }).format(nowStart);
    };

    updatePrevNextState();

    nextBtn.addEventListener(
      'click',
      async () => {
        try {
          if (!this.#sdk) return;
          await this.#sdk.next('month');
          await this.#renderFromData(this.#sdk.data);
        } catch (error) {
          console.error('Navigation next error', error);
        }
      },
      { signal: this.#abortController?.signal },
    );

    prevBtn.addEventListener(
      'click',
      async () => {
        try {
          if (!this.#sdk || prevBtn.disabled) return;
          await this.#sdk.previous('month');
          await this.#renderFromData(this.#sdk.data);
        } catch (error) {
          console.error('Navigation prev error', error);
        }
      },
      { signal: this.#abortController?.signal },
    );

    this.#prefetchAvails(daysMeta, renderIdString);
  }

  #prefetchAvails(daysMeta, renderIdString) {
    (async () => {
      let index = 0;
      const concurrency = 3;
      const workers = new Array(concurrency).fill(null).map(async () => {
        while (true) {
          const currentIndex = index++;
          if (currentIndex >= daysMeta.length) return;

          const item = daysMeta[currentIndex];
          const { day, badge, isInactive } = item;

          if (badge.dataset.render !== renderIdString) continue;

          if (isInactive) {
            badge.classList.remove('available', 'error');
            badge.setAttribute('aria-label', 'Unavailable');
            badge.setAttribute('title', 'Unavailable');
            continue;
          }

          try {
            const slots = await day.getAvailabilities();
            if (badge.dataset.render !== renderIdString) continue;

            if (slots && slots.length > 0) {
              badge.classList.add('available');
              badge.classList.remove('error');
              badge.setAttribute('aria-label', 'Available');
              badge.setAttribute('title', `${slots.length} time(s) available`);
              badge.closest('button')?.removeAttribute('disabled');
            } else {
              badge.classList.remove('available', 'error');
              badge.setAttribute('aria-label', 'No availability');
              badge.setAttribute('title', 'No availability');
            }
          } catch (error) {
            if (badge.dataset.render !== renderIdString) continue;

            const isAuth = error && (error.status === 401 || /401|Unauthorized/i.test(String(error.message || '')));
            badge.classList.add('error');
            badge.classList.remove('available');
            badge.setAttribute('aria-label', isAuth ? 'Authorization required' : 'Error fetching');
            badge.setAttribute('title', isAuth ? 'Auth error' : 'Error');
            console.warn('Prefetch error for', day.date, error);
          }
        }
      });

      await Promise.all(workers);
    })().catch((error) => {
      console.warn('Prefetch worker error', error);
    });
  }

  #attachAddToCartReserveHandler() {
    try {
      if (!this.#form) return;

      this.#realAddToCartButton = this.#findAddToCartButton(this.#form);
      if (!this.#realAddToCartButton) {
        console.warn('Sesami: no add-to-cart button found');
        return;
      }

      this.#realAddToCartButton.disabled = true;
      this.#realAddToCartButton.style.display = 'none';

      if (this.#proxyButton) return;

      const proxyButton = document.createElement('button');
      proxyButton.type = 'button';
      proxyButton.className = this.#realAddToCartButton.className;
      proxyButton.innerHTML = this.#realAddToCartButton.innerHTML;
      proxyButton.disabled = true;
      proxyButton.dataset.sesamiProxy = 'true';

      this.#realAddToCartButton.insertAdjacentElement('afterend', proxyButton);
      this.#proxyButton = proxyButton;

      this.#syncConfirmationGroupVisibility(true);

      proxyButton.addEventListener(
        'click',
        () => {
          this.#handleProxyClick(proxyButton).catch((error) => {
            console.error('Sesami proxy click failed', error);
          });
        },
        { signal: this.#abortController?.signal },
      );
    } catch (error) {
      console.error('Sesami: attachAddToCartReserveHandler error', error);
    }
  }

  async #handleProxyClick(proxyButton) {
    if (!this.#pendingSlot) {
      console.warn('Sesami: no slot selected');
      return;
    }

    if (this.#isSubmittingReservation) {
      console.warn('Sesami: submission already in progress');
      return;
    }

    this.#isSubmittingReservation = true;
    proxyButton.disabled = true;
    this.#syncConfirmationGroupVisibility(true);

    const existingErrorMessage = this.#mount?.querySelector('.sesami-reserve-error');
    if (existingErrorMessage) existingErrorMessage.remove();

    const tokenInput = this.#ensureHiddenFieldsWrapper()?.querySelector('[name="properties[_reservation_token]"]');
    if (tokenInput instanceof HTMLInputElement) tokenInput.value = '';

    try {
      const reserveResult = await this.#sdk.reserve(this.#pendingSlot);
      const reservationToken =
        typeof reserveResult === 'string'
          ? reserveResult
          : (reserveResult && (reserveResult.token || reserveResult.reservationToken || reserveResult.id)) || '';

      if (!reservationToken) {
        console.error('Sesami reserve() returned no token:', reserveResult);
        this.#isSubmittingReservation = false;
        proxyButton.disabled = false;
        this.#syncConfirmationGroupVisibility(false);
        this.#mount?.appendChild(
          Object.assign(document.createElement('div'), {
            className: 'sesami-reserve-error',
            textContent: 'Failed to reserve the selected time slot. Please try again.',
          }),
        );
        return;
      }

      let bookingOptions = null;
      try {
        const cart = await this.#fetchCart();
        bookingOptions = this.#buildBookingOptionsFromCart(cart);
      } catch (cartError) {
        console.warn('Sesami: unable to build booking options from cart', cartError);
      }

      this.#upsertSesamiHiddenFields(this.#pendingSlot, reservationToken, bookingOptions);

      this.dispatchEvent(
        new CustomEvent('sesami:reservation-created', {
          bubbles: true,
          detail: {
            slot: this.#pendingSlot,
            reservationToken,
            bookingOptions,
          },
        }),
      );

      // Persist the access token so booking-contact-form.js can reuse it on the
      // confirmation page. The token is URL-scoped by Sesami — re-fetching it on
      // a different page URL causes a 410 on book().
      if (this.#sdk?.accesstoken) {
        try {
          sessionStorage.setItem('sesami_access_token', this.#sdk.accesstoken);
        } catch (storageError) {
          console.warn('Sesami: could not persist access token', storageError);
        }
      }

      if (this.#realAddToCartButton) {
        // Suppress the cart drawer opening for this booking add-to-cart
        window.__suppressCartDrawerForBooking = true;
        this.#realAddToCartButton.disabled = false;
        this.#realAddToCartButton.click();
      }

      this.#isSubmittingReservation = false;

      // Step 1 → Step 2: swap calendar for the confirmation view
      this.#showAlreadyInCart();
    } catch (error) {
      console.error('Sesami: xreserve() failed', error);
      this.#isSubmittingReservation = false;
      proxyButton.disabled = false;

      const tokenInputAfterError = this.#ensureHiddenFieldsWrapper()?.querySelector(
        '[name="properties[_reservation_token]"]',
      );
      if (tokenInputAfterError instanceof HTMLInputElement) tokenInputAfterError.value = '';

      const errorMessageElement = document.createElement('div');
      errorMessageElement.className = 'sesami-reserve-error';
      errorMessageElement.style.cssText = 'color:#900;margin-top:8px;font-size:13px;';
      errorMessageElement.textContent = error.message || 'Failed to reserve the selected time slot. Please try again.';
      this.#mount?.appendChild(errorMessageElement);
    }
  }
}

if (!customElements.get('sesami-booking-component')) {
  customElements.define('sesami-booking-component', SesamiBookingComponent);
}
